import { useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import type { EventInput } from '@fullcalendar/core';
import { PlusIcon } from '../../assets/icons';

type Category = 'election' | 'party' | 'festival' | 'meeting';

const CATEGORY: Record<Category, { color: string; label: string }> = {
  election: { color: '#004ac6', label: 'Election/Campaign' },
  party: { color: '#943700', label: 'Party Events' },
  festival: { color: '#8b5cf6', label: 'Festivals' },
  meeting: { color: '#10b981', label: 'Meetings' },
};
const LEGEND_ORDER: Category[] = ['election', 'party', 'festival', 'meeting'];

/** October 2024 events. */
const EVENTS: EventInput[] = [
  { title: 'Core Committee Meeting', date: '2024-10-01', category: 'meeting' },
  { title: 'Gandhi Jayanti Holiday', date: '2024-10-02', category: 'festival' },
  { title: 'Phase 2 Registration', date: '2024-10-07', category: 'election' },
  { title: 'Youth Wing Rally', date: '2024-10-08', category: 'party' },
  { title: 'Media Briefing', date: '2024-10-08', category: 'meeting' },
  { title: 'Electoral List Finalization', date: '2024-10-09', category: 'election' },
  { title: 'Dussehra Festival', date: '2024-10-11', category: 'festival' },
  { title: 'Submission Deadline', date: '2024-10-16', category: 'election' },
  { title: 'General Body Meet', date: '2024-10-22', category: 'party' },
  { title: 'Diwali Prep', date: '2024-10-30', category: 'festival' },
  { title: 'Diwali Holiday', date: '2024-10-31', category: 'festival' },
].map((e) => ({ ...e, classNames: [`cat-${e.category}`], allDay: true }));

const VIEWS = [
  { label: 'Month', view: 'dayGridMonth' },
  { label: 'Week', view: 'timeGridWeek' },
  { label: 'Agenda', view: 'listMonth' },
] as const;

export default function CalendarPage() {
  const calendarRef = useRef<FullCalendar>(null);
  const [activeView, setActiveView] = useState<string>('dayGridMonth');

  const changeView = (view: string) => {
    setActiveView(view);
    calendarRef.current?.getApi().changeView(view);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-[-0.6px] text-ink">Global Calendar</h1>
          <p className="text-sm text-ink-secondary">
            Manage central election timelines, party schedules, and major milestones.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg bg-brand px-6 py-2.5 text-xs font-medium tracking-[0.6px] text-white shadow-btn transition-colors hover:bg-brand-dark"
          >
            <PlusIcon className="size-[11px]" />
            Add Event
          </button>

          <div className="flex overflow-hidden rounded-lg border border-[#d1e2ff] p-px">
            {VIEWS.map((v) => (
              <button
                key={v.view}
                type="button"
                onClick={() => changeView(v.view)}
                className={[
                  'px-4 py-2 text-xs font-medium tracking-[0.6px] text-[#434655] transition-colors',
                  activeView === v.view ? 'bg-[#d1e2ff]' : 'bg-white hover:bg-[#eef4ff]',
                ].join(' ')}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-6 xl:flex-row">
        {/* Categories */}
        <aside className="h-fit rounded-xl border border-line bg-white p-5 shadow-card xl:w-[195px] xl:shrink-0">
          <h2 className="mb-4 mt-0 text-lg font-medium text-ink">Categories</h2>
          <div className="flex flex-col gap-3">
            {LEGEND_ORDER.map((cat) => (
              <div key={cat} className="flex items-center gap-3">
                <span
                  className="size-4 shrink-0 rounded-sm"
                  style={{ backgroundColor: CATEGORY[cat].color }}
                />
                <span className="text-sm text-[#434655]">{CATEGORY[cat].label}</span>
              </div>
            ))}
          </div>
        </aside>

        {/* Calendar */}
        <div className="chunaav-calendar min-w-0 flex-1 overflow-hidden rounded-xl border border-line bg-white shadow-card">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin]}
            initialView="dayGridMonth"
            initialDate="2024-10-01"
            headerToolbar={{ left: 'title prev,next', center: '', right: 'today' }}
            buttonText={{ today: 'Today' }}
            events={EVENTS}
            eventDisplay="block"
            dayMaxEvents={3}
            fixedWeekCount={false}
            height="auto"
            firstDay={0}
            dayHeaderFormat={{ weekday: 'short' }}
          />
        </div>
      </div>
    </div>
  );
}
